import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Trash2, Edit3, Save, X, LayoutGrid, Inbox, Settings2, Users, Image as ImageIcon, GripVertical, ShieldCheck, Download, Copy, Check, Plug, MessageCircle, Building2 } from 'lucide-react';
import Logo from '../../components/Logo';
import { adminMe, adminLogout, fetchProjects, createProject, updateProject, deleteProject, fetchLeads, deleteLead, updateLeadStatus, addLeadNote, assignLeadOwner, fetchSettings, updateSettings, fetchDevelopers, createDeveloper, updateDeveloper, deleteDeveloper, fetchAgents, createAgent, updateAgent, deleteAgent, fetchUsers, createUser, updateUser, deleteUser, uploadFile, exportLeads, fetchCrmInfo, rotateCrmKey } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { DEFAULT_PRIVACY } from '../../lib/privacyDefault';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { renderLeadTemplate, openWhatsApp, DEFAULT_WA_TEMPLATE, DEFAULT_EMAIL_TEMPLATE, SAMPLE_LEAD } from '../../lib/whatsapp';

const LEAD_STATUSES = ['New', 'Contacted', 'Closed'];
const STATUS_STYLES = {
  New: 'bg-gobra-green/10 text-gobra-green border-gobra-green/30',
  Contacted: 'bg-amber-100 text-amber-700 border-amber-300',
  Closed: 'bg-gray-100 text-gray-500 border-gray-300',
};

const formatLeadDate = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const LeadCard = ({ lead: l, agents = [], canManage = true, waTemplate = '', onStatus, onDelete, onAddNote, onAssign }) => {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const notes = l.notes || [];
  const submit = async () => {
    const text = note.trim();
    if (!text || saving) return;
    setSaving(true);
    try { await onAddNote(l.id, text); setNote(''); } finally { setSaving(false); }
  };
  const ownerAgent = agents.find((a) => a.id === l.ownerId);
  const ownerWa = (ownerAgent?.whatsapp || '').replace(/\D/g, '');
  const sendToAgent = () => {
    if (!ownerWa) return;
    openWhatsApp(ownerWa, renderLeadTemplate(waTemplate, l));
  };
  return (
    <div className="p-4 border-b border-gobra-sand">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-gobra-slate">{l.name} <span className="text-muted-foreground font-normal">· {l.mobile}</span></div>
          <div className="text-sm text-muted-foreground">{l.email}</div>
          {l.project && <div className="text-xs text-gobra-terracotta mt-1">{l.project}</div>}
          {l.note && <div className="text-sm text-gobra-slate mt-1">{l.note}</div>}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">Owner:</span>
            {canManage ? (
              <select value={l.ownerId || ''} onChange={(e) => onAssign(l.id, e.target.value)}
                data-testid={`lead-owner-${l.id}`}
                className="text-xs rounded-md border border-gobra-sand bg-white px-2 py-1 cursor-pointer focus:outline-none text-gobra-slate">
                <option value="">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            ) : (
              <span className="text-xs font-medium text-gobra-slate" data-testid={`lead-owner-label-${l.id}`}>{l.ownerName || 'Unassigned'}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {l.createdAt && <div className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`lead-date-${l.id}`}>{formatLeadDate(l.createdAt)}</div>}
          <select value={l.status || 'New'} onChange={(e) => onStatus(l.id, e.target.value)}
            data-testid={`lead-status-${l.id}`}
            className={`text-xs font-semibold rounded-full px-2.5 py-1 border cursor-pointer focus:outline-none ${STATUS_STYLES[l.status || 'New']}`}>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={sendToAgent} disabled={!ownerWa}
            title={ownerWa ? `Send to ${ownerAgent?.name} on WhatsApp` : 'Assign an advisor who has a WhatsApp number'}
            data-testid={`lead-send-whatsapp-${l.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-[#25D366] text-white hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition">
            <MessageCircle className="h-3.5 w-3.5" /> Send to agent
          </button>
          <button onClick={() => onDelete(l.id)} className="text-destructive disabled:opacity-30" disabled={!canManage} style={{ display: canManage ? undefined : 'none' }} data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3">
        <button onClick={() => setOpen((o) => !o)} data-testid={`lead-notes-toggle-${l.id}`}
          className="text-xs font-semibold text-gobra-green hover:underline">
          {open ? 'Hide' : 'Follow-up notes'}{notes.length > 0 ? ` (${notes.length})` : ''}
        </button>
        {open && (
          <div className="mt-2 space-y-2" data-testid={`lead-notes-${l.id}`}>
            {notes.length === 0 ? <p className="text-xs text-muted-foreground">No notes yet.</p> : notes.map((n) => (
              <div key={n.id} className="text-xs bg-gobra-cream/60 border border-gobra-sand rounded-lg px-3 py-2">
                <div className="text-gobra-slate whitespace-pre-wrap">{n.text}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{n.author} · {formatLeadDate(n.createdAt)}</div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder="Add a follow-up note…" className="h-9 text-sm"
                data-testid={`lead-note-input-${l.id}`} />
              <Button onClick={submit} disabled={!note.trim() || saving} size="sm"
                data-testid={`lead-note-save-${l.id}`}
                className="h-9 bg-gobra-green hover:bg-gobra-green-dark text-white">Add</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Drag & drop / click image uploader -> uploads to object storage, stores the returned URL
const ImageDrop = ({ value, onChange, label }) => {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = React.useRef(null);
  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true); setErr('');
    try {
      const res = await uploadFile(file);
      onChange(res.url);
    } catch (e) {
      setErr('Upload failed. Please try again.');
    } finally { setUploading(false); }
  };
  return (
    <div>
      {label && <Label className="text-gobra-slate text-xs uppercase tracking-wide">{label}</Label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`mt-1 cursor-pointer rounded-xl border-2 border-dashed p-4 flex items-center gap-4 transition-colors ${drag ? 'border-gobra-green bg-gobra-green/5' : 'border-gobra-sand bg-white'}`}>
        {value ? <img src={value} alt="preview" className="h-16 w-24 object-cover rounded-lg" />
          : <div className="h-16 w-24 rounded-lg bg-gobra-sand flex items-center justify-center"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>}
        <div className="text-sm text-muted-foreground">
          <div className="font-medium text-gobra-slate">{uploading ? 'Uploading…' : 'Drag & drop or click to upload'}</div>
          <div className="text-xs">PNG, JPG or WEBP</div>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>
      {err && <p className="text-xs text-destructive mt-1" data-testid="image-upload-error">{err}</p>}
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="...or paste an image URL" className="mt-2 text-xs" />
    </div>
  );
};

// Generic uploader for non-image assets (PDF brochures, MP4 videos)
const FileUpload = ({ value, onChange, label, accept, hint }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');
  const inputRef = React.useRef(null);
  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true); setErr(''); setProgress(0);
    try {
      const res = await uploadFile(file, setProgress);
      onChange(res.url);
    } catch (e) {
      setErr('Upload failed. Please try again.');
    } finally { setUploading(false); }
  };
  return (
    <div>
      {label && <Label className="text-gobra-slate text-xs uppercase tracking-wide">{label}</Label>}
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="border-gobra-green text-gobra-green">
          <ImageIcon className="h-4 w-4 mr-2" /> {uploading ? `Uploading… ${progress}%` : 'Upload file'}
        </Button>
        {value && <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-gobra-green underline truncate max-w-[240px]">{value.split('/').pop()}</a>}
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>
      {err && <p className="text-xs text-destructive mt-1">{err}</p>}
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={hint} className="mt-2 text-xs" />
    </div>
  );
};

const empty = { name: '', developer: '', developerId: '', area: '', priceAed: 0, plan: '', handover: '', beds: '', type: 'Apartments', types: [], sqft: '', furnishing: '', lat: '', lng: '', eoi: false, eoiDate: '', status: 'offplan', category: 'luxury', trending: false, featured: false, inCarousel: false, acceptsUsdt: false, image: '', gallery: [], youtubeUrl: '', videoUrl: '', brochureUrl: '', offer: '', offerExpiry: '', description: '', amenities: [], order: 999 };

const Field = ({ label, children }) => (
  <div><Label className="text-gobra-slate text-xs uppercase tracking-wide">{label}</Label><div className="mt-1">{children}</div></div>
);

const ProjectForm = ({ initial, developers, onCancel, onSaved }) => {
  const [f, setF] = useState({ ...empty, ...initial, gallery: (initial?.gallery || []) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    const toNum = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
    const payload = { ...f, priceAed: Number(f.priceAed) || 0, order: Number(f.order) || 999,
      lat: toNum(f.lat), lng: toNum(f.lng),
      gallery: typeof f.gallery === 'string' ? f.gallery.split('\n').map((s) => s.trim()).filter(Boolean) : f.gallery,
      amenities: typeof f.amenities === 'string' ? f.amenities.split(',').map((s) => s.trim()).filter(Boolean) : f.amenities };
    const dev = developers.find((d) => d.id === f.developerId);
    if (dev) payload.developer = dev.name;
    try {
      if (initial?.id) await updateProject(initial.id, { ...payload, id: initial.id });
      else await createProject(payload);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-gobra-sand rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-title text-xl font-semibold text-gobra-slate">{initial?.id ? 'Edit Project' : 'New Project'}</h3>
        <button onClick={onCancel}><X className="h-5 w-5 text-muted-foreground" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name"><Input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Developer">
          <select value={f.developerId} onChange={(e) => set('developerId', e.target.value)} className="w-full border border-input rounded-md h-10 px-3">
            <option value="">Select developer</option>
            {developers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Area"><Input value={f.area} onChange={(e) => set('area', e.target.value)} /></Field>
        <Field label="Price (AED)"><Input type="number" value={f.priceAed} onChange={(e) => set('priceAed', e.target.value)} /></Field>
        <Field label="Payment Plan (e.g. 20 / 55 / 25)"><Input value={f.plan} onChange={(e) => set('plan', e.target.value)} /></Field>
        <Field label="Handover (e.g. Q3 2029)"><Input value={f.handover} onChange={(e) => set('handover', e.target.value)} /></Field>
        <Field label="Bedrooms (e.g. Studio, 1 - 4)"><Input value={f.beds} onChange={(e) => set('beds', e.target.value)} placeholder="Studio, 1, 2 - 4..." /></Field>
        <Field label="Type (select one or more)">
          <div className="flex flex-wrap gap-4 pt-2">
            {['Apartments', 'Villas', 'Townhouses', 'Commercial', 'Offices'].map((ty) => {
              const types = Array.isArray(f.types) ? f.types : [];
              const checked = types.includes(ty);
              return (
                <label key={ty} className="flex items-center gap-2 text-sm text-gobra-slate">
                  <input type="checkbox" checked={checked} data-testid={`type-${ty.toLowerCase()}`}
                    onChange={(e) => {
                      const next = e.target.checked ? [...types, ty] : types.filter((x) => x !== ty);
                      set('types', next); set('type', next.join(' · '));
                    }} />
                  {ty}
                </label>
              );
            })}
          </div>
        </Field>
        <Field label="Area (SQFT)"><Input value={f.sqft || ''} onChange={(e) => set('sqft', e.target.value)} data-testid="project-sqft-input" placeholder="e.g. 1,250 sqft" /></Field>
        <Field label="Furnishing">
          <select value={f.furnishing || ''} onChange={(e) => set('furnishing', e.target.value)} className="w-full border border-input rounded-md h-10 px-3" data-testid="project-furnishing-select">
            <option value="">Not specified</option>
            <option value="Furnished">Furnished</option>
            <option value="Semi-Furnished">Semi-Furnished</option>
            <option value="Shell &amp; Core">Shell &amp; Core</option>
          </select>
        </Field>
        <Field label="Map — Latitude (optional, overrides area)"><Input value={f.lat ?? ''} onChange={(e) => set('lat', e.target.value)} data-testid="project-lat-input" placeholder="e.g. 25.1972" /></Field>
        <Field label="Map — Longitude (optional, overrides area)"><Input value={f.lng ?? ''} onChange={(e) => set('lng', e.target.value)} data-testid="project-lng-input" placeholder="e.g. 55.2744" /></Field>
        <Field label="Status">
          <select value={f.status} onChange={(e) => set('status', e.target.value)} className="w-full border border-input rounded-md h-10 px-3">
            <option value="offplan">Off-Plan</option><option value="comingsoon">Coming Soon</option><option value="ready">Ready</option><option value="rent">For Rent</option>
          </select>
        </Field>
        <Field label="Category">
          <select value={f.category} onChange={(e) => set('category', e.target.value)} className="w-full border border-input rounded-md h-10 px-3">
            <option value="luxury">Luxury</option><option value="villas">Villas</option><option value="flats">Apartments</option>
          </select>
        </Field>
        <Field label="Order"><Input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} /></Field>
      </div>
      <ImageDrop value={f.image} onChange={(v) => set('image', v)} label="Main Image" />
      <Field label="YouTube Video Tour URL (walkthrough)"><Input value={f.youtubeUrl || ''} onChange={(e) => set('youtubeUrl', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." /></Field>
      <FileUpload value={f.videoUrl} onChange={(v) => set('videoUrl', v)} label="Uploaded Video (mp4 — takes priority over YouTube)" accept="video/*" hint="...or paste a video URL" />
      <FileUpload value={f.brochureUrl} onChange={(v) => set('brochureUrl', v)} label="Brochure (PDF — enables Download Brochure button)" accept="application/pdf" hint="...or paste a PDF URL" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Limited Offer text (shown as a banner)"><Input value={f.offer || ''} onChange={(e) => set('offer', e.target.value)} placeholder="e.g. Complimentary Lamborghini with every villa" /></Field>
        <Field label="Offer valid until (YYYY-MM-DD)"><Input type="date" value={f.offerExpiry || ''} onChange={(e) => set('offerExpiry', e.target.value)} /></Field>
      </div>
      <Field label="Gallery Image URLs (one per line)"><Textarea rows={3} value={Array.isArray(f.gallery) ? f.gallery.join('\n') : f.gallery} onChange={(e) => set('gallery', e.target.value)} /></Field>
      <Field label="Amenities (comma separated)"><Textarea rows={2} value={Array.isArray(f.amenities) ? f.amenities.join(', ') : f.amenities} onChange={(e) => set('amenities', e.target.value)} /></Field>
      <Field label="Description"><Textarea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} /></Field>
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2"><Switch checked={f.trending} onCheckedChange={(v) => set('trending', v)} /> <span className="text-sm text-gobra-slate">Trending</span></label>
        <label className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={(v) => set('featured', v)} /> <span className="text-sm text-gobra-slate">Featured</span></label>
        <label className="flex items-center gap-2"><Switch checked={f.inCarousel} onCheckedChange={(v) => set('inCarousel', v)} /> <span className="text-sm text-gobra-slate">Homepage Carousel</span></label>
        <label className="flex items-center gap-2"><Switch checked={f.eoi} onCheckedChange={(v) => set('eoi', v)} /> <span className="text-sm text-gobra-slate">EOI Open</span></label>
        {f.eoi && (
          <Field label="EOI Date"><Input type="date" value={f.eoiDate || ''} onChange={(e) => set('eoiDate', e.target.value)} data-testid="eoi-date-input" /></Field>
        )}
        <label className="flex items-center gap-2"><Switch checked={f.acceptsUsdt} onCheckedChange={(v) => set('acceptsUsdt', v)} /> <span className="text-sm text-gobra-slate">Accepts USDT</span></label>
      </div>
      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} className="bg-gobra-green hover:bg-gobra-green-dark text-white"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save'}</Button>
        <Button onClick={onCancel} variant="outline">Cancel</Button>
      </div>
    </div>
  );
};

const CrmIntegrationCard = () => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState('');
  const [rotating, setRotating] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const endpoint = `${base}/api/crm/leads`;
  useEffect(() => { fetchCrmInfo().then(setInfo).catch(() => {}); }, []);
  const copy = (text, which) => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(''), 1500);
  };
  const rotate = async () => {
    if (!window.confirm('Regenerate the CRM API key? The current key will stop working immediately and you must update your CRM with the new key.')) return;
    setRotating(true);
    try {
      const res = await rotateCrmKey();
      setInfo((prev) => ({ ...(prev || {}), apiKey: res.apiKey }));
    } finally { setRotating(false); }
  };
  const curlSample = info ? `curl "${endpoint}?since=2026-01-01&limit=500" \\\n  -H "X-API-Key: ${info.apiKey}"` : '';
  return (
    <div className="mt-8 bg-white border border-gobra-sand rounded-xl p-6 max-w-2xl" data-testid="crm-integration-card">
      <div className="flex items-center gap-2 mb-1">
        <Plug className="h-5 w-5 text-gobra-green" />
        <h3 className="font-title text-xl font-bold text-gobra-slate">CRM Integration API</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Pull all leads and their follow-up notes into your CRM, or set a webhook URL in Settings above to receive each new lead in real time. Authenticate with the API key below (header <code className="text-xs bg-gobra-cream px-1 rounded">X-API-Key</code>). Supports <code className="text-xs bg-gobra-cream px-1 rounded">?since=ISO&amp;status=New&amp;limit=&amp;offset=</code>.</p>

      <Label className="text-xs text-muted-foreground">Endpoint (GET)</Label>
      <div className="flex gap-2 mb-4 mt-1">
        <Input readOnly value={endpoint} data-testid="crm-endpoint" className="font-mono text-xs" />
        <Button type="button" variant="outline" onClick={() => copy(endpoint, 'ep')} data-testid="crm-copy-endpoint" className="shrink-0">
          {copied === 'ep' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>

      <Label className="text-xs text-muted-foreground">API Key (keep this secret)</Label>
      <div className="flex gap-2 mb-2 mt-1">
        <Input readOnly value={info?.apiKey || '••••••••'} data-testid="crm-api-key" className="font-mono text-xs" />
        <Button type="button" variant="outline" onClick={() => copy(info?.apiKey || '', 'key')} data-testid="crm-copy-key" className="shrink-0">
          {copied === 'key' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <Button type="button" variant="outline" onClick={rotate} disabled={rotating} data-testid="crm-rotate-key"
        className="mb-4 text-xs text-destructive border-destructive/30 hover:bg-destructive/5">
        {rotating ? 'Regenerating…' : 'Regenerate Key'}
      </Button>

      <Label className="text-xs text-muted-foreground">Example request</Label>
      <pre className="mt-1 bg-gobra-slate text-gobra-cream text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap" data-testid="crm-curl">{curlSample}</pre>
    </div>
  );
};

const SettingsForm = () => {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { fetchSettings().then(setS); }, []);
  if (!s) return <p className="text-muted-foreground">Loading...</p>;
  const set = (k, v) => { setS((p) => ({ ...p, [k]: v })); setSaved(false); };
  const save = async () => { await updateSettings(s); setSaved(true); };
  return (
    <div className="bg-white border border-gobra-sand rounded-xl p-5 space-y-4 max-w-2xl">
      <h3 className="font-title text-xl font-semibold text-gobra-slate">Company Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Company Name"><Input value={s.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Phone"><Input value={s.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
        <Field label="WhatsApp"><Input value={s.whatsapp || ''} onChange={(e) => set('whatsapp', e.target.value)} /></Field>
        <Field label="Email"><Input value={s.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Website (shown as a clickable link in generated project PDFs)"><Input value={s.website || ''} onChange={(e) => set('website', e.target.value)} data-testid="settings-website-input" placeholder="www.gobracapital.ae" /></Field>
        <Field label="Address"><Input value={s.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
        <Field label="Instagram URL"><Input value={s.instagram || ''} onChange={(e) => set('instagram', e.target.value)} /></Field>
        <Field label="Instagram Handle"><Input value={s.instagramHandle || ''} onChange={(e) => set('instagramHandle', e.target.value)} /></Field>
      </div>
      <Field label="Footer License / Legal Text (shown at the bottom of every page)">
        <Textarea rows={2} value={s.footerLicense || ''} onChange={(e) => set('footerLicense', e.target.value)}
          placeholder="e.g. GOBRA Capital Real Estate — Trade Licence No. 000000 | RERA ORN 00000" />
      </Field>
      <Field label="Privacy Policy (shown on the /privacy page, linked in the footer)">
        <p className="text-xs text-muted-foreground mb-2">Formatting: start a line with <code>#</code> for a section heading, <code>-</code> for a bullet point, and leave a blank line between paragraphs. The Contact Us block is added automatically from your company details above.</p>
        <Textarea rows={16} value={s.privacyPolicy || DEFAULT_PRIVACY} onChange={(e) => set('privacyPolicy', e.target.value)}
          data-testid="settings-privacy-input" className="font-mono text-xs" />
      </Field>
      <Field label="Daily Lead Digest Recipients (comma-separated emails — receives the 8am summary of new leads)">
        <Input value={s.digestRecipients || ''} onChange={(e) => set('digestRecipients', e.target.value)}
          data-testid="digest-recipients-input"
          placeholder="e.g. info@gobracapital.ae, sales@gobracapital.ae" />
      </Field>
      <Field label="CRM Webhook URL (optional — each new lead is POSTed here in real time as JSON)">
        <Input value={s.crmWebhookUrl || ''} onChange={(e) => set('crmWebhookUrl', e.target.value)}
          data-testid="crm-webhook-input"
          placeholder="https://your-crm.com/webhooks/gobra-leads" />
      </Field>
      <Field label="WhatsApp Message to Advisors (sent when forwarding a lead — placeholders: {name} {phone} {email} {project} {message} {status} {owner} {date})">
        <Textarea rows={6} value={s.whatsappTemplate ?? ''} onChange={(e) => set('whatsappTemplate', e.target.value)}
          data-testid="whatsapp-template-input"
          placeholder={DEFAULT_WA_TEMPLATE} className="font-mono text-xs" />
        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Live Preview</Label>
          <div className="mt-1 max-w-sm bg-[#dcf8c6] text-gobra-slate text-sm rounded-xl rounded-tl-sm p-3 whitespace-pre-wrap shadow-sm" data-testid="whatsapp-template-preview">
            {renderLeadTemplate(s.whatsappTemplate, SAMPLE_LEAD)}
          </div>
        </div>
      </Field>
      <Field label="Lead Notification Email to the Team (sent to the digest recipients when a new lead arrives — same placeholders)">
        <Textarea rows={6} value={s.emailTemplate ?? ''} onChange={(e) => set('emailTemplate', e.target.value)}
          data-testid="email-template-input"
          placeholder={DEFAULT_EMAIL_TEMPLATE} className="font-mono text-xs" />
        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Live Preview</Label>
          <div className="mt-1 max-w-md bg-white border border-gobra-sand text-gobra-slate text-sm rounded-lg p-3 whitespace-pre-wrap" data-testid="email-template-preview">
            {renderLeadTemplate(s.emailTemplate || DEFAULT_EMAIL_TEMPLATE, SAMPLE_LEAD)}
          </div>
        </div>
      </Field>
      <div className="flex items-center gap-3">
        <Button onClick={save} className="bg-gobra-green hover:bg-gobra-green-dark text-white"><Save className="h-4 w-4 mr-2" /> Save Settings</Button>
        {saved && <span className="text-gobra-green text-sm">Saved!</span>}
      </div>
    </div>
  );
};

const AgentsManager = () => {
  const [agents, setAgents] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => fetchAgents().then(setAgents);
  useEffect(() => { load(); }, []);
  const roles = [
    { v: 'property_advisor', l: 'Property Advisor' },
    { v: 'senior_advisor', l: 'Senior Property Advisor' },
    { v: 'associate_director', l: 'Associate Director' },
  ];
  const save = async (a) => {
    const payload = { ...a, order: Number(a.order) || 999 };
    if (a.id) await updateAgent(a.id, payload); else await createAgent(payload);
    setEditing(null); load();
  };
  const remove = async (id) => { if (window.confirm('Delete this advisor?')) { await deleteAgent(id); load(); } };

  if (editing) {
    const a = editing;
    const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
    return (
      <div className="bg-white border border-gobra-sand rounded-xl p-5 space-y-4 max-w-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-title text-xl font-semibold text-gobra-slate">{a.id ? 'Edit Advisor' : 'New Advisor'}</h3>
          <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <Field label="Name"><Input value={a.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Role">
          <select value={a.role || 'property_advisor'} onChange={(e) => set('role', e.target.value)} className="w-full border border-input rounded-md h-10 px-3">
            {roles.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </Field>
        <Field label="Spoken Languages (e.g. EN, AR, FR)"><Input value={a.langs || ''} onChange={(e) => set('langs', e.target.value)} /></Field>
        <Field label="Login Email (MUST exactly match this advisor's Team Access login email — this is how their assigned leads appear in their dashboard)"><Input type="email" value={a.email || ''} onChange={(e) => set('email', e.target.value)} data-testid="advisor-email-input" placeholder="advisor@gobracapital.ae" /></Field>
        <Field label="WhatsApp Number (with country code, e.g. +971501234567 — used to forward leads)"><Input value={a.whatsapp || ''} onChange={(e) => set('whatsapp', e.target.value)} data-testid="advisor-whatsapp-input" placeholder="+971501234567" /></Field>
        <Field label="Display Order"><Input type="number" value={a.order ?? 999} onChange={(e) => set('order', e.target.value)} /></Field>
        <ImageDrop value={a.photo} onChange={(v) => set('photo', v)} label="Photo" />
        <div className="flex gap-3">
          <Button onClick={() => save(a)} className="bg-gobra-green hover:bg-gobra-green-dark text-white"><Save className="h-4 w-4 mr-2" /> Save</Button>
          <Button onClick={() => setEditing(null)} variant="outline">Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-title text-2xl font-bold text-gobra-slate">{agents.length} Advisors</h2>
        <Button onClick={() => setEditing({ role: 'property_advisor', langs: 'EN', order: 999 })} className="bg-gobra-terracotta hover:bg-gobra-green text-white"><Plus className="h-4 w-4 mr-2" /> New Advisor</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((a) => (
          <div key={a.id} className="bg-white border border-gobra-sand rounded-xl p-4 flex gap-3">
            {a.photo ? <img src={a.photo} alt={a.name} className="h-16 w-16 rounded-lg object-cover shrink-0" /> : <div className="h-16 w-16 rounded-lg bg-gobra-sand shrink-0" />}
            <div className="min-w-0">
              <h3 className="font-title font-semibold text-gobra-slate truncate">{a.name}</h3>
              <p className="text-xs text-muted-foreground">{a.role?.replace(/_/g, ' ')}</p>
              {a.email ? (
                <p className="text-xs text-gobra-slate mt-1 truncate" title={a.email}>✉ {a.email}</p>
              ) : (
                <p className="text-xs text-amber-600 mt-1" title="Set this advisor's login email so assigned leads appear in their dashboard">⚠ No login email — assigned leads won't show for this advisor</p>
              )}
              <p className="text-xs text-gobra-terracotta mt-1">{a.langs}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(a)} className="text-xs flex items-center gap-1 text-gobra-blue"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => remove(a.id)} className="text-xs flex items-center gap-1 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DevelopersManager = ({ onChange }) => {
  const [developers, setDevelopers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => fetchDevelopers().then(setDevelopers);
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); if (onChange) onChange(); };
  const save = async (d) => {
    if (!d.name || !d.name.trim()) return;
    const payload = { name: d.name.trim(), tagline: (d.tagline || '').trim(), logo: d.logo || '', order: Number(d.order) || 999 };
    if (d.id) await updateDeveloper(d.id, payload); else await createDeveloper(payload);
    setEditing(null); refresh();
  };
  const remove = async (id) => { if (window.confirm('Remove this developer from the Trusted Partners list?')) { await deleteDeveloper(id); refresh(); } };

  if (editing) {
    const d = editing;
    const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
    return (
      <div className="bg-white border border-gobra-sand rounded-xl p-5 space-y-4 max-w-xl" data-testid="developer-form">
        <div className="flex items-center justify-between">
          <h3 className="font-title text-xl font-semibold text-gobra-slate">{d.id ? 'Edit Developer' : 'New Developer'}</h3>
          <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <Field label="Developer Name"><Input value={d.name || ''} onChange={(e) => set('name', e.target.value)} data-testid="developer-name-input" placeholder="e.g. EMAAR" /></Field>
        <Field label="Tagline (optional, shown under the name)"><Input value={d.tagline || ''} onChange={(e) => set('tagline', e.target.value)} data-testid="developer-tagline-input" placeholder="e.g. Master Developer" /></Field>
        <Field label="Display Order"><Input type="number" value={d.order ?? 999} onChange={(e) => set('order', e.target.value)} data-testid="developer-order-input" /></Field>
        <ImageDrop value={d.logo} onChange={(v) => set('logo', v)} label="Logo (PNG with transparent background works best)" />
        <div className="flex gap-3">
          <Button onClick={() => save(d)} className="bg-gobra-green hover:bg-gobra-green-dark text-white" data-testid="developer-save-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
          <Button onClick={() => setEditing(null)} variant="outline">Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-title text-2xl font-bold text-gobra-slate">{developers.length} Developers</h2>
        <Button onClick={() => setEditing({ order: 999 })} className="bg-gobra-terracotta hover:bg-gobra-green text-white" data-testid="new-developer-btn"><Plus className="h-4 w-4 mr-2" /> New Developer</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">These appear in the homepage "Trusted Partner of the Most Prominent Developers" strip and in the developer dropdown when adding a project.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {developers.map((d) => (
          <div key={d.id} className="bg-white border border-gobra-sand rounded-xl p-4 flex items-start gap-3" data-testid={`developer-card-${d.id}`}>
            {d.logo
              ? <div className="h-11 w-11 rounded-lg bg-gobra-sand flex items-center justify-center shrink-0 overflow-hidden"><img src={d.logo} alt={d.name} className="h-full w-full object-contain p-1" /></div>
              : <div className="h-11 w-11 rounded-lg bg-gobra-sand flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-gobra-green" /></div>}
            <div className="min-w-0 flex-1">
              <h3 className="font-title font-semibold text-gobra-slate truncate">{d.name}</h3>
              {d.tagline ? <p className="text-[11px] tracking-widest text-muted-foreground uppercase mt-0.5 truncate">{d.tagline}</p> : null}
              <div className="flex gap-3 mt-2">
                <button onClick={() => setEditing(d)} className="text-xs flex items-center gap-1 text-gobra-blue" data-testid={`edit-developer-${d.id}`}><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => remove(d.id)} className="text-xs flex items-center gap-1 text-destructive" data-testid={`delete-developer-${d.id}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'marketing', agentId: '' });
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // user object being edited
  const [editError, setEditError] = useState('');
  const load = () => fetchUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); fetchAgents().then(setAgents).catch(() => {}); }, []);
  const advisorAgents = agents.filter((a) => (a.role || '').includes('advisor') || true);
  const agentName = (id) => agents.find((a) => a.id === id)?.name || '';

  const add = async (e) => {
    e.preventDefault(); setError('');
    try { await createUser(form); setForm({ name: '', email: '', password: '', role: 'marketing', agentId: '' }); load(); }
    catch (err) { setError(err?.response?.data?.detail || 'Could not create user'); }
  };
  const remove = async (email) => { if (window.confirm(`Remove ${email}?`)) { await deleteUser(email); load(); } };
  const saveEdit = async () => {
    setEditError('');
    try {
      await updateUser(editing._originalEmail, {
        name: editing.name, email: editing.email, role: editing.role,
        agentId: editing.agentId || '', password: editing.password || undefined,
      });
      setEditing(null); load();
    } catch (err) { setEditError(err?.response?.data?.detail || 'Could not update user'); }
  };
  const roleBadge = (role) => `inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${role === 'admin' ? 'bg-gobra-green/10 text-gobra-green' : role === 'manager' ? 'bg-gobra-terracotta/10 text-gobra-terracotta' : role === 'property_advisor' ? 'bg-amber-100 text-amber-700' : 'bg-gobra-blue/10 text-gobra-blue'}`;

  return (
    <div className="max-w-2xl">
      <h2 className="font-title text-2xl font-bold text-gobra-slate mb-1">Team Access</h2>
      <p className="text-xs text-muted-foreground mb-5">Only the administrator can create accounts. Managers get full access except team management. Marketing can edit content. Property Advisers see only the leads assigned to them (linked to an advisor profile below) plus projects.</p>
      <form onSubmit={add} className="bg-white border border-gobra-sand rounded-xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-6">
        <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wide text-gobra-slate">Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="user-name-input" placeholder="e.g. John Smith" /></div>
        <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wide text-gobra-slate">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" data-testid="user-email-input" /></div>
        <div><Label className="text-xs uppercase tracking-wide text-gobra-slate">Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1" data-testid="user-password-input" /></div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-gobra-slate">Role</Label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full border border-input rounded-md h-10 px-3" data-testid="user-role-select">
            <option value="marketing">Marketing</option>
            <option value="property_advisor">Property Adviser</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {form.role === 'property_advisor' && (
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-gobra-slate">Linked Advisor Profile</Label>
            <select value={form.agentId} onChange={(e) => setForm({ ...form, agentId: e.target.value })} className="mt-1 w-full border border-input rounded-md h-10 px-3" data-testid="user-agent-select">
              <option value="">— Select advisor profile —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">This login will see leads assigned to the chosen advisor. No email match needed.</p>
          </div>
        )}
        <div className="md:col-span-4 flex items-center gap-3">
          <Button type="submit" className="bg-gobra-green hover:bg-gobra-green-dark text-white" data-testid="create-user-btn"><Plus className="h-4 w-4 mr-2" /> Create User</Button>
          {error && <span className="text-destructive text-sm">{error}</span>}
        </div>
      </form>
      <div className="bg-white border border-gobra-sand rounded-xl overflow-hidden">
        {users.map((u) => (
          <div key={u.email} className="p-4 border-b border-gobra-sand flex items-center justify-between">
            <div>
              {u.name && <div className="font-semibold text-gobra-slate">{u.name}</div>}
              <div className={`${u.name ? 'text-sm text-muted-foreground' : 'font-medium text-gobra-slate'}`}>{u.email}</div>
              <span className={roleBadge(u.role)}>{u.role?.replace(/_/g, ' ')}</span>
              {u.role === 'property_advisor' && (
                u.agentId
                  ? <span className="ml-2 text-xs text-gobra-slate">→ {agentName(u.agentId) || 'linked'}</span>
                  : <span className="ml-2 text-xs text-amber-600">⚠ not linked to an advisor profile</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { setEditing({ ...u, _originalEmail: u.email, password: '' }); setEditError(''); }} className="text-gobra-blue" data-testid={`edit-user-${u.email}`}><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => remove(u.email)} className="text-destructive" data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <h3 className="font-title text-xl font-bold text-gobra-slate mb-4">Edit Team Member</h3>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wide text-gobra-slate">Full Name</Label><Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1" data-testid="edit-user-name" /></div>
              <div><Label className="text-xs uppercase tracking-wide text-gobra-slate">Email</Label><Input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1" data-testid="edit-user-email" /></div>
              <div><Label className="text-xs uppercase tracking-wide text-gobra-slate">New Password (leave blank to keep)</Label><Input type="text" value={editing.password || ''} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="mt-1" data-testid="edit-user-password" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-gobra-slate">Role</Label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="mt-1 w-full border border-input rounded-md h-10 px-3" data-testid="edit-user-role">
                  <option value="marketing">Marketing</option>
                  <option value="property_advisor">Property Adviser</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {editing.role === 'property_advisor' && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-gobra-slate">Linked Advisor Profile</Label>
                  <select value={editing.agentId || ''} onChange={(e) => setEditing({ ...editing, agentId: e.target.value })} className="mt-1 w-full border border-input rounded-md h-10 px-3" data-testid="edit-user-agent">
                    <option value="">— Select advisor profile —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
              {editError && <p className="text-destructive text-sm">{editError}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-user-cancel">Cancel</Button>
              <Button onClick={saveEdit} className="bg-gobra-green hover:bg-gobra-green-dark text-white" data-testid="edit-user-save"><Save className="h-4 w-4 mr-2" /> Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('projects');
  const [role, setRole] = useState('');
  const [projects, setProjects] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [waTemplate, setWaTemplate] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('All');
  const [leadOwnerFilter, setLeadOwnerFilter] = useState('All');
  const [leadFrom, setLeadFrom] = useState('');
  const [leadTo, setLeadTo] = useState('');
  const [editing, setEditing] = useState(null); // project obj or {} for new
  const [dragIndex, setDragIndex] = useState(null);

  const persistOrder = async (list) => {
    setProjects(list);
    await Promise.allSettled(list.map((p, i) => updateProject(p.id, { ...p, order: i + 1 })));
  };
  const onDropCard = (i) => {
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); return; }
    const list = [...projects];
    const [moved] = list.splice(dragIndex, 1);
    list.splice(i, 0, moved);
    setDragIndex(null);
    persistOrder(list);
  };

  const loadProjects = useCallback(() => fetchProjects().then(setProjects), []);

  useEffect(() => {
    document.title = 'Admin | GOBRA';
    adminMe().then((u) => { setRole(u.role); if (u.role === 'property_advisor') setTab('leads'); }).catch(() => { navigate('/admin/login'); });
    loadProjects();
    fetchDevelopers().then(setDevelopers);
    fetchLeads().then(setLeads).catch(() => {});
    fetchAgents().then(setAgents).catch(() => {});
    fetchSettings().then((s) => setWaTemplate(s?.whatsappTemplate || '')).catch(() => {});
  }, [navigate, loadProjects]);

  const logout = async () => { try { await adminLogout(); } catch (e) { /* best-effort logout; redirect regardless of API result */ } navigate('/admin/login'); };
  const onSaved = () => { setEditing(null); loadProjects(); };
  const removeProject = async (id) => { if (window.confirm('Delete this project?')) { await deleteProject(id); loadProjects(); } };
  const removeLead = async (id) => { await deleteLead(id); fetchLeads().then(setLeads); };
  const changeLeadStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await updateLeadStatus(id, status); } catch { fetchLeads().then(setLeads); }
  };
  const saveLeadNote = async (id, text) => {
    const res = await addLeadNote(id, text);
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, notes: [...(l.notes || []), res.note] } : l)));
  };
  const changeLeadOwner = async (id, ownerId) => {
    const lead = leads.find((l) => l.id === id);
    const prevOwner = lead?.ownerId || '';
    const res = await assignLeadOwner(id, ownerId);
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ownerId: res.ownerId, ownerName: res.ownerName } : l)));
    // Auto-open WhatsApp compose when assigned to an advisor with a WhatsApp number
    if (ownerId && ownerId !== prevOwner) {
      const agent = agents.find((a) => a.id === ownerId);
      if (agent?.whatsapp) {
        const updated = { ...(lead || {}), ownerId: res.ownerId, ownerName: res.ownerName };
        openWhatsApp(agent.whatsapp, renderLeadTemplate(waTemplate, updated));
      }
    }
  };
  const filteredLeads = leads.filter((l) => {
    if (leadStatusFilter !== 'All' && (l.status || 'New') !== leadStatusFilter) return false;
    if (leadOwnerFilter !== 'All') {
      if (leadOwnerFilter === 'unassigned') { if (l.ownerId) return false; }
      else if (l.ownerId !== leadOwnerFilter) return false;
    }
    if (leadFrom && l.createdAt && new Date(l.createdAt) < new Date(leadFrom + 'T00:00:00')) return false;
    if (leadTo && l.createdAt && new Date(l.createdAt) > new Date(leadTo + 'T23:59:59')) return false;
    return true;
  });
  const exportLeadsXlsx = async () => {
    const params = {};
    if (leadStatusFilter !== 'All') params.status = leadStatusFilter;
    if (leadOwnerFilter !== 'All' && leadOwnerFilter !== 'unassigned') params.owner = leadOwnerFilter;
    if (leadFrom) params.dfrom = leadFrom;
    if (leadTo) params.dto = leadTo;
    const blob = await exportLeads(params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gobra-leads.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const isAdvisor = role === 'property_advisor';
  const canEditContent = ['admin', 'manager', 'marketing'].includes(role);
  const tabs = [
    { key: 'projects', label: 'Projects', icon: LayoutGrid },
    ...(isAdvisor ? [] : [{ key: 'agents', label: 'Advisors', icon: Users }]),
    ...(isAdvisor ? [] : [{ key: 'developers', label: 'Developers', icon: Building2 }]),
    { key: 'leads', label: 'Leads', icon: Inbox },
    ...(isAdvisor ? [] : [{ key: 'settings', label: 'Settings', icon: Settings2 }]),
    ...(role === 'admin' ? [{ key: 'users', label: 'Team Access', icon: ShieldCheck }] : []),
  ];

  return (
    <div className="min-h-screen bg-gobra-sand/40">
      <header className="bg-white border-b border-gobra-sand sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <Logo variant="dark" />
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-gobra-slate">View Site</Button>
            <Button onClick={logout} variant="outline" className="border-gobra-terracotta text-gobra-terracotta"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        <div className="flex gap-2 mb-6">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => { setTab(tb.key); setEditing(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${tab === tb.key ? 'bg-gobra-green text-white' : 'bg-white text-gobra-slate border border-gobra-sand'}`}>
              <tb.icon className="h-4 w-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === 'projects' && (
          editing !== null ? (
            <ProjectForm initial={editing} developers={developers} onCancel={() => setEditing(null)} onSaved={onSaved} />
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-title text-2xl font-bold text-gobra-slate">{projects.length} Projects</h2>
                  <p className="text-xs text-muted-foreground mt-1">Drag cards to reorder how they appear on the homepage.</p>
                </div>
                <Button onClick={() => setEditing({})} disabled={!canEditContent} className="bg-gobra-terracotta hover:bg-gobra-green text-white disabled:opacity-40" data-testid="new-project-btn" style={{ display: canEditContent ? undefined : 'none' }}><Plus className="h-4 w-4 mr-2" /> New Project</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.map((p, idx) => (
                  <div key={p.id}
                    draggable={canEditContent}
                    onDragStart={() => canEditContent && setDragIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => canEditContent && onDropCard(idx)}
                    className={`bg-white border rounded-xl p-4 flex gap-4 transition-colors ${canEditContent ? 'cursor-move' : ''} ${dragIndex === idx ? 'border-gobra-green ring-2 ring-gobra-green/30' : 'border-gobra-sand'}`}>
                    <div className="flex items-center text-muted-foreground" style={{ display: canEditContent ? undefined : 'none' }}><GripVertical className="h-4 w-4" /></div>
                    <img src={p.image} alt={p.name} className="h-20 w-28 object-cover rounded-lg shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-title font-semibold text-gobra-slate truncate">{p.name}</h3>
                      <p className="text-xs text-muted-foreground">{p.developer} · {p.area}</p>
                      <p className="text-sm text-gobra-green font-semibold mt-1">AED {(p.priceAed/1000000).toFixed(1)}M {p.acceptsUsdt && '· USDT'}</p>
                      {canEditContent && (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => setEditing(p)} className="text-xs flex items-center gap-1 text-gobra-blue"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                          <button onClick={() => removeProject(p.id)} className="text-xs flex items-center gap-1 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {tab === 'leads' && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-title text-2xl font-bold text-gobra-slate">{filteredLeads.length} Enquiries</h2>
              <Button onClick={exportLeadsXlsx} disabled={leads.length === 0} data-testid="export-leads-btn"
                className="bg-gobra-green hover:bg-gobra-green-dark text-white">
                <Download className="h-4 w-4 mr-2" /> Export to Excel
              </Button>
            </div>
            <div className="flex flex-wrap items-end gap-3 mb-4 bg-gobra-cream/60 border border-gobra-sand rounded-xl p-3">
              <div className="flex gap-1">
                {['All', ...LEAD_STATUSES].map((s) => {
                  const count = s === 'All' ? leads.length : leads.filter((l) => (l.status || 'New') === s).length;
                  return (
                    <button key={s} onClick={() => setLeadStatusFilter(s)} data-testid={`lead-filter-${s.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${leadStatusFilter === s ? 'bg-gobra-green text-white' : 'bg-white text-gobra-slate border border-gobra-sand hover:border-gobra-green'}`}>
                      {s} <span className={`ml-1 ${leadStatusFilter === s ? 'opacity-90' : 'text-muted-foreground'}`} data-testid={`lead-count-${s.toLowerCase()}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-end gap-2">
                {!isAdvisor && (
                  <div><Label className="text-xs text-muted-foreground">Advisor</Label>
                    <select value={leadOwnerFilter} onChange={(e) => setLeadOwnerFilter(e.target.value)}
                      data-testid="lead-owner-filter"
                      className="block h-9 rounded-md border border-gobra-sand bg-white px-2 text-sm text-gobra-slate">
                      <option value="All">All advisors</option>
                      <option value="unassigned">Unassigned</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                )}
                <div><Label className="text-xs text-muted-foreground">From</Label>
                  <Input type="date" value={leadFrom} onChange={(e) => setLeadFrom(e.target.value)} data-testid="lead-from-date" className="h-9" /></div>
                <div><Label className="text-xs text-muted-foreground">To</Label>
                  <Input type="date" value={leadTo} onChange={(e) => setLeadTo(e.target.value)} data-testid="lead-to-date" className="h-9" /></div>
                {(leadFrom || leadTo || leadStatusFilter !== 'All' || leadOwnerFilter !== 'All') && (
                  <Button variant="ghost" onClick={() => { setLeadFrom(''); setLeadTo(''); setLeadStatusFilter('All'); setLeadOwnerFilter('All'); }}
                    data-testid="lead-clear-filters" className="h-9 text-xs">Clear</Button>
                )}
              </div>
            </div>
            <div className="bg-white border border-gobra-sand rounded-xl overflow-hidden">
              {filteredLeads.length === 0 ? <p className="p-6 text-muted-foreground">No enquiries match these filters.</p> : filteredLeads.map((l) => (
                <LeadCard key={l.id} lead={l} agents={agents} canManage={canEditContent} waTemplate={waTemplate} onStatus={changeLeadStatus} onDelete={removeLead} onAddNote={saveLeadNote} onAssign={changeLeadOwner} />
              ))}
            </div>
          </div>
        )}

        {tab === 'settings' && <SettingsForm />}
        {tab === 'settings' && role === 'admin' && <CrmIntegrationCard />}
        {tab === 'agents' && <AgentsManager />}
        {tab === 'developers' && <DevelopersManager onChange={() => fetchDevelopers().then(setDevelopers)} />}
        {tab === 'users' && role === 'admin' && <UsersManager />}
      </div>
    </div>
  );
};

export default AdminDashboard;
